import { CSSResultGroup, TemplateResult, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import componentStyles from './tufts-search-bar-dynamic.css.lit';
import { OutlineElement } from '../../base/outline-element/outline-element';

/**
 * The tuftsSearchBarDynamic component
 * @element tufts-search-bar-dynamic
 *
 * @slot - A default slot
 */
@customElement('tufts-search-bar-dynamic')
export class TuftsSearchBarDynamic extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  /**
   * This property determines the duration of the placeholder animation.
   **/
  @property({
    type: Number,
    reflect: true,
    attribute: 'placeholder-duration',
  })
  placeholderDuration = 8;
  @state() showLoading = false;
  @query('slot') slotElement: HTMLSlotElement;
  private escapeListenerAdded = false;

  // Keeps track of the active slide index
  nextSlideIndex = 0;

  connectedCallback() {
    super.connectedCallback();

    // A setTimeout is used to wait half a second before setting up the facet
    // event listeners. This ensures that all DOM elements are fully loaded,
    // as the complete HTML is sent from Drupal.
    setTimeout(() => {
      this.setupFacetEventListener();
    }, 500);
  }

  // Called after the component's first render
  firstUpdated(): void {
    this.initializePlaceholders();
    this.setupFormEventListeners();
  }

  setupFacetEventListener() {
    const checkboxes = document.querySelectorAll(
      'input[type="checkbox"].facets-checkbox, span.facet-summary-item--clear'
    );

    if (checkboxes.length > 0) {
      checkboxes.forEach(checkbox => {
        checkbox.addEventListener('click', () => this.handleButtonClick());
      });
    }
  }

  setupFormEventListeners() {
    const nodes = this.slotElement.assignedNodes();

    const form = nodes.find(node => node instanceof HTMLFormElement) as
      | HTMLFormElement
      | undefined;
    if (form) {
      const submitButton = form.querySelector(
        'input[type="submit"], button[type="submit"]'
      );
      if (submitButton) {
        submitButton.addEventListener('click', () => this.handleButtonClick());
      }
    }
  }
  handleButtonClick() {
    this.showLoading = true;
    if (!this.escapeListenerAdded) {
      document.addEventListener('keydown', event =>
        this._handleEscapeKey(event)
      );
      this.escapeListenerAdded = true;
    }
  }

  _handleEscapeKey(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.showLoading = false;
    }
  }

  // Initializes the placeholders and sets up the animation
  initializePlaceholders() {
    // Set the placeholder duration CSS property
    this.style.setProperty(
      '--placeholder-duration',
      this.placeholderDuration + 's'
    );
    // Get all the placeholder slides
    const slides = this.querySelectorAll(
      '[slot=placeholder]'
    ) as NodeListOf<HTMLElement>;
    if (slides.length) {
      const inputElement = this.querySelector('input');
      if (inputElement) {
        // Initialize first slide with 'active' class
        slides[0].classList.add('active');

        slides.forEach(slide => {
          // Set the slide as hidden for accessibility
          slide.setAttribute('aria-hidden', 'true');

          // 'animationend' event triggers the next slide to get 'active' class
          slide.addEventListener('animationend', () => {
            // Remove the 'active' class from the slide
            slide.classList.remove('active');

            // Advance Slide Index
            this.nextSlideIndex = (this.nextSlideIndex + 1) % slides.length;

            // Set next slide
            const currentSlide = slides[this.nextSlideIndex];
            currentSlide.classList.add('active');
          });
          // Insert the slide after the input element
          inputElement.after(slide);
          // By adding 'placeholder' + 'required' attributes, we can use :valid rule in CSS
          inputElement.setAttribute('placeholder', '');
          inputElement.setAttribute('required', '');
        });
      }
    }
  }

  render(): TemplateResult {
    return html` <slot></slot>
      ${this.showLoading ? this.displayPending() : null}`;
  }
  displayPending() {
    return html`
      <div class="lds-ring">
        <div></div>
        <div></div>
        <div></div>
        <div></div>
      </div>
    `;
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._handleEscapeKey);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-search-bar-dynamic': TuftsSearchBarDynamic;
  }
}
